import { useCallback, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import TabPanel from "@mui/lab/TabPanel";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";

import CheckboxFormField from "components/checkbox-form-field";

import type { SelectSaleConfigurationValues } from "../word-processor-select-sale-configuration-dialog.component.js";
import SettingsAccordionSummary from "./settings-accordion-summary.component.js";

interface TotalsSettingsProps {
  readonly expanded?: boolean;
  readonly onChange?: (event: React.SyntheticEvent, expanded: boolean) => void;
}
export default function TotalsSettings({
  expanded,
  onChange
}: TotalsSettingsProps) {
  const { control, setValue, getValues } =
    useFormContext<SelectSaleConfigurationValues>();

  const totalsFieldNames = [
    "total_show_services_dispensing_fees",
    "total_show_discounts",
    "total_tax",
    "total_amount",
    "total_patient_payments",
    "total_insurance_payments",
    "amount_due_from_insurance",
    "amount_due_from_patient",
    "balance_due",
    "total_show_cost_of_ha"
  ] as const;

  const watchedValues = useWatch({
    control,
    name: totalsFieldNames
  });

  const selectedCount = useMemo(() => {
    return Array.isArray(watchedValues)
      ? watchedValues.filter(Boolean).length
      : 0;
  }, [watchedValues]);

  const handleSelectAll = useCallback(
    (event: any) => {
      event.preventDefault();
      event.stopPropagation();
      const includeHearingAids = getValues("include_hearing_aids");

      (setValue as any)("total_show_services_dispensing_fees", true);
      (setValue as any)("total_show_discounts", true);
      (setValue as any)("total_tax", true);
      (setValue as any)("total_amount", true);
      (setValue as any)("total_patient_payments", true);
      (setValue as any)("total_insurance_payments", true);
      (setValue as any)("amount_due_from_insurance", true);
      (setValue as any)("amount_due_from_patient", true);
      (setValue as any)("balance_due", true);
      // Only set total_show_cost_of_ha if include_hearing_aids is true
      if (includeHearingAids) {
        (setValue as any)("total_show_cost_of_ha", true);
      }
    },
    [setValue, getValues]
  );

  const handleDeselectAll = useCallback(
    (event: any) => {
      event.preventDefault();
      event.stopPropagation();
      (setValue as any)("total_show_services_dispensing_fees", false);
      (setValue as any)("total_show_discounts", false);
      (setValue as any)("total_tax", false);
      (setValue as any)("total_amount", false);
      (setValue as any)("total_patient_payments", false);
      (setValue as any)("total_insurance_payments", false);
      (setValue as any)("amount_due_from_insurance", false);
      (setValue as any)("amount_due_from_patient", false);
      (setValue as any)("balance_due", false);
      // Always set this to false on deselect all
      (setValue as any)("total_show_cost_of_ha", false);
    },
    [setValue]
  );

  return (
    <TabPanel value="totals">
      <Accordion expanded={expanded} onChange={onChange}>
        <SettingsAccordionSummary
          title="Invoice Totals section settings:"
          expanded={expanded}
          onSelectAll={handleSelectAll}
          onDeselectAll={handleDeselectAll}
          selectedCount={selectedCount}
        />
        <AccordionDetails>
          <Stack ml={2} mt={-2} mb={2}>
            <FormControlLabel
              label="Show Services/Dispensing Fees"
              sx={{ mb: -1 }}
              control={
                <CheckboxFormField
                  name="total_show_services_dispensing_fees"
                  control={control}
                />
              }
            />

            <FormControlLabel
              label="Show Total Cost of Hearing Aids"
              sx={{ mb: -1 }}
              control={
                <CheckboxFormField
                  name="total_show_cost_of_ha"
                  control={control}
                  sx={{ ml: 0 }}
                />
              }
            />
            <FormControlLabel
              label="Show Discounts"
              sx={{ mb: -1 }}
              control={
                <CheckboxFormField
                  name="total_show_discounts"
                  control={control}
                />
              }
            />
            <FormControlLabel
              label="Show Total Tax"
              sx={{ mb: -1 }}
              control={<CheckboxFormField name="total_tax" control={control} />}
            />

            <FormControlLabel
              label="Show Total Amount"
              sx={{ mb: -1 }}
              control={
                <CheckboxFormField
                  name="total_amount"
                  control={control}
                  sx={{ ml: 0 }}
                />
              }
            />

            <FormControlLabel
              label="Show Total Patient Payments"
              sx={{ mb: -1 }}
              control={
                <CheckboxFormField
                  name="total_patient_payments"
                  control={control}
                  sx={{ ml: 0 }}
                />
              }
            />

            <FormControlLabel
              label="Show Total Insurance Payments"
              sx={{ mb: -1 }}
              control={
                <CheckboxFormField
                  name="total_insurance_payments"
                  control={control}
                  sx={{ ml: 0 }}
                />
              }
            />

            <FormControlLabel
              label="Show Amount Due From Insurance"
              sx={{ mb: -1 }}
              control={
                <CheckboxFormField
                  name="amount_due_from_insurance"
                  control={control}
                  sx={{ ml: 0 }}
                />
              }
            />

            <FormControlLabel
              label="Show Amount Due From Patient"
              sx={{ mb: -1 }}
              control={
                <CheckboxFormField
                  name="amount_due_from_patient"
                  control={control}
                  sx={{ ml: 0 }}
                />
              }
            />

            <FormControlLabel
              label="Show Balance Due"
              sx={{ mb: -1 }}
              control={
                <CheckboxFormField
                  name="balance_due"
                  control={control}
                  sx={{ ml: 0 }}
                />
              }
            />
          </Stack>
        </AccordionDetails>
      </Accordion>
    </TabPanel>
  );
}
